<!-- src/views/AnalysisView.vue -->
<template>
  <div class="p-3 p-md-4 max-w-7xl mx-auto">
    <div class="card shadow-sm analysis-card">
      <div class="card-body">
        <h3 class="text-center mb-4">JOBS & HOURS ANALYSIS</h3>

        <div class="d-flex align-items-center justify-content-center flex-wrap gap-2 mb-3">
          <select v-model="selectedOptionLabel" class="form-select" style="max-width: 320px;">
            <option v-for="chartButton in chartButtons" :key="chartButton.label" :value="chartButton.label">
              {{ chartButton.label }}
            </option>
          </select>
          <button
            @click="runQuery(selectedOption)"
            class="btn btn-primary"
            :disabled="isLoading"
          >
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
            Show Graph
          </button>
        </div>

        <div class="d-flex align-items-center justify-content-center flex-wrap gap-3 mb-4">
          <div class="d-flex align-items-center gap-2">
            <label class="fw-medium">Employee</label>
            <Multiselect
              v-model="selectedOfficerId"
              :options="officers.map(o => ({ value: o.id, label: o.name }))"
              placeholder="Select officer..."
              :searchable="true"
              :clearable="true"
              :disabled="selectedOption.requires !== 'officer'"
              style="min-width: 220px; --ms-border-color: #dee2e6; --ms-radius: 0.375rem; --ms-px: 0.75rem; --ms-py: 0.25rem;"
            />
          </div>
          <div class="d-flex align-items-center gap-2">
            <label class="fw-medium">Client</label>
            <Multiselect
              v-model="selectedClientId"
              :options="clients.map(c => ({ value: c.id, label: c.name }))"
              placeholder="Select client..."
              :searchable="true"
              :clearable="true"
              :disabled="selectedOption.requires !== 'client'"
              style="min-width: 220px; --ms-border-color: #dee2e6; --ms-radius: 0.375rem; --ms-px: 0.75rem; --ms-py: 0.25rem;"
            />
          </div>
          <div class="d-flex align-items-center gap-2">
            <label class="fw-medium">Start Date</label>
            <input type="date" v-model="startDate" class="border rounded px-3 py-1">
          </div>
          <div class="d-flex align-items-center gap-2">
            <label class="fw-medium">End Date</label>
            <input type="date" v-model="endDate" class="border rounded px-3 py-1">
          </div>
        </div>

        <p v-if="errorMessage" class="text-center text-danger">{{ errorMessage }}</p>
        <template v-if="chartTitle">
          <h5 class="text-center mb-1">{{ chartTitle }}</h5>
          <p v-if="chartPoints.length" class="text-center text-muted small mb-3">
            Total: {{ totalLabel }}
          </p>
        </template>
        <BarChart :points="chartPoints" :value-label="valueLabel" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import Multiselect from '@vueform/multiselect'
import '@vueform/multiselect/themes/default.css'
import BarChart from '@/components/BarChart.vue'
import { isBoss } from '@/utils/auth'

const props = defineProps({
  baseUrl: { type: String, required: true }
})

const router = useRouter()

const officers = ref([])
const clients = ref([])
const selectedOfficerId = ref(null)
const selectedClientId = ref(null)
const startDate = ref('')
const endDate = ref('')
const isLoading = ref(false)
const errorMessage = ref('')
const chartTitle = ref('')
const chartPoints = ref([])
const chartMetric = ref('JobCount')

// Ordered by expected usage frequency (most client-relevant/attractive first),
// per owner's instinct rather than measured usage data — revisit if real usage
// patterns turn out to differ.
const chartButtons = [
  { label: 'Jobs Vs Years', groupBy: 'Year', metric: 'JobCount', requires: null },
  { label: 'Hrs Vs Years', groupBy: 'Year', metric: 'Hours', requires: null },
  { label: 'Jobs Vs Months', groupBy: 'Month', metric: 'JobCount', requires: null },
  { label: 'Hrs Vs Month', groupBy: 'Month', metric: 'Hours', requires: null },
  { label: 'Jobs Vs DaysOfWeek', groupBy: 'DayOfWeek', metric: 'JobCount', requires: null },
  { label: 'Hrs Vs DaysOfWeek', groupBy: 'DayOfWeek', metric: 'Hours', requires: null },
  { label: 'Client Hrs Vs Weekdays', groupBy: 'DayOfWeek', metric: 'Hours', requires: 'client' },
  { label: 'Client Hrs Vs Month', groupBy: 'Month', metric: 'Hours', requires: 'client' },
  { label: 'Emp Hrs Vs Month', groupBy: 'Month', metric: 'Hours', requires: 'officer' },
  { label: 'Emp Jobs Vs Dates', groupBy: 'Date', metric: 'JobCount', requires: 'officer' },
  { label: 'Emp Hrs Vs Dates', groupBy: 'Date', metric: 'Hours', requires: 'officer' }
]

const selectedOptionLabel = ref(chartButtons[0].label)
const selectedOption = computed(() =>
  chartButtons.find(b => b.label === selectedOptionLabel.value) || chartButtons[0]
)

const valueLabel = computed(() => (chartMetric.value === 'Hours' ? 'Hours' : 'Job Count'))

const totalLabel = computed(() => {
  const total = chartPoints.value.reduce((sum, p) => sum + p.value, 0)
  const rounded = Math.round(total * 100) / 100
  return chartMetric.value === 'Hours' ? `${rounded} hours` : `${rounded} jobs`
})

const loadLookupData = () => {
  const cachedData = localStorage.getItem("lookupData")
  if (cachedData) {
    const parsed = JSON.parse(cachedData)
    officers.value = parsed.officers || []
    clients.value = parsed.companies || []
  }
}

const runQuery = async (chartButton) => {
  errorMessage.value = ''

  if (!startDate.value || !endDate.value) {
    errorMessage.value = 'Please select both a start date and an end date.'
    return
  }
  if (chartButton.requires === 'officer' && !selectedOfficerId.value) {
    errorMessage.value = 'Please select an employee for this chart.'
    return
  }
  if (chartButton.requires === 'client' && !selectedClientId.value) {
    errorMessage.value = 'Please select a client for this chart.'
    return
  }

  isLoading.value = true
  try {
    const response = await fetch(props.baseUrl + "Analysis/JobStats", {
      method: "POST",
      headers: { "Authorization": "Bearer " + localStorage.token, "Content-Type": "application/json" },
      body: JSON.stringify({
        startDate: startDate.value,
        endDate: endDate.value,
        groupBy: chartButton.groupBy,
        metric: chartButton.metric,
        securityOfficerId: chartButton.requires === 'officer' ? selectedOfficerId.value : null,
        clientId: chartButton.requires === 'client' ? selectedClientId.value : null
      })
    })
    if (!response.ok) throw new Error(await response.text())
    chartPoints.value = await response.json()
    chartTitle.value = chartButton.label
    chartMetric.value = chartButton.metric
  } catch (e) {
    errorMessage.value = 'Failed to load chart: ' + e.message
    chartPoints.value = []
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  if (!isBoss()) {
    router.replace("/schedule")
    return
  }

  loadLookupData()

  const today = new Date()
  const monthAgo = new Date()
  monthAgo.setDate(today.getDate() - 30)
  startDate.value = monthAgo.toISOString().slice(0, 10)
  endDate.value = today.toISOString().slice(0, 10)
})
</script>

<style scoped>
.analysis-card {
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}
</style>
